import type {
  QueryParamsWithDefaults,
  SetQueryParams,
} from '@/modules/common/context/QueryParams/models';

import {pick, isEqual} from 'lodash';
import {useCallback, useMemo} from 'react';
import {defaultMemoize} from 'reselect';

import {useQueryParams} from '@/modules/common/context/QueryParams';
import {SEARCH_PARAMS} from '@/modules/common/context/QueryParams/models';

const pickSearchParams = defaultMemoize(
  (queryParams: QueryParamsWithDefaults) => {
    return pick(queryParams, ...SEARCH_PARAMS);
  },
  {
    equalityCheck: (
      previousQueryParams: QueryParamsWithDefaults,
      nextQueryParams: QueryParamsWithDefaults
    ) => {
      return isEqual(
        pick(previousQueryParams, ...SEARCH_PARAMS),
        pick(nextQueryParams, ...SEARCH_PARAMS)
      );
    },
  }
);

export const useSearchParams = () => {
  const [queryParams, setQueryParams] = useQueryParams();
  // IMPORTANT: We NEED to make sure value is stable so that we can use it in `useEffect` dependencies.
  const value = useMemo(() => pickSearchParams(queryParams), [queryParams]);

  const setSearchQueryParams = useCallback<SetQueryParams>(
    (params, options) => {
      setQueryParams(params, {
        ...options,
        state: {...options?.state, changedBy: 'search'},
      });
    },
    [setQueryParams]
  );

  return [value, setSearchQueryParams] as const;
};
